import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useNavigate } from "react-router-dom";
import { Check, Mail, Trash2, ExternalLink, MoreVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useNotificationActions } from "@/hooks/app/useNotificationActions";
import type { Notification } from "@/hooks/app/useNotifications";
import { getNotificationVisual, getPriorityBarClass, getNotificationRoute, getRollupCount } from "./notificationMeta";
import { cn } from "@/lib/utils";

interface NotificationItemProps {
  notification: Notification;
  onClose?: () => void;
  variant?: "popup" | "page";
}

/** Tempo relativo compacto (estilo inbox): agora, 5min, 2h, 3d, 12/08. */
function shortRelativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return "agora";
  if (minutes < 60) return `${minutes}min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return format(new Date(iso), "dd/MM", { locale: ptBR });
}

export function NotificationItem({ notification, onClose, variant = "page" }: NotificationItemProps) {
  const navigate = useNavigate();
  const { markAsRead, markAsUnread, deleteWithUndo } = useNotificationActions();
  const { Icon, toneClass } = getNotificationVisual(notification);
  const route = getNotificationRoute(notification);
  const rollupCount = getRollupCount(notification);

  const handleClick = () => {
    if (!notification.is_read) markAsRead(notification.id);
    if (route) {
      navigate(route);
      onClose?.();
    }
  };

  const handleMarkAsRead = (e: React.MouseEvent | Event) => {
    e.stopPropagation();
    if (!notification.is_read) markAsRead(notification.id);
  };

  const handleMarkAsUnread = (e: React.MouseEvent | Event) => {
    e.stopPropagation();
    if (notification.is_read) markAsUnread(notification.id);
  };

  const handleDelete = (e: React.MouseEvent | Event) => {
    e.stopPropagation();
    deleteWithUndo(notification);
  };

  const handleOpen = (e: React.MouseEvent | Event) => {
    e.stopPropagation();
    handleClick();
  };

  return (
    <div
      className={cn(
        "group p-3 hover:bg-hover cursor-pointer transition-colors relative",
        !notification.is_read && "bg-primary/5"
      )}
      onClick={handleClick}
    >
      <div className={cn("absolute left-0 top-0 bottom-0 w-1", getPriorityBarClass(notification.priority))} />

      {/* Ações rápidas: hover/foco no desktop; sempre visíveis em touch (pointer:coarse). */}
      {variant === "popup" && (
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-0.5 rounded-md bg-hover/95 opacity-0 shadow-sm transition-opacity group-hover:opacity-100 focus-within:opacity-100 [@media(pointer:coarse)]:opacity-100">
          {!notification.is_read ? (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
              onClick={handleMarkAsRead}
              aria-label="Marcar como lida"
              title="Marcar como lida"
            >
              <Check className="h-4 w-4" />
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
              onClick={handleMarkAsUnread}
              aria-label="Marcar como não lida"
              title="Marcar como não lida"
            >
              <Mail className="h-4 w-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
            onClick={handleDelete}
            aria-label="Excluir"
            title="Excluir"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      )}

      <div className={cn("flex items-start gap-3 ml-2", variant === "popup" && "[@media(pointer:coarse)]:pr-16")}>
        <div className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-full mt-0.5", toneClass)}>
          <Icon className="h-4 w-4" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {!notification.is_read && (
              <span className="h-2 w-2 shrink-0 rounded-full bg-primary" aria-label="Não lida" />
            )}
            <p className={cn("min-w-0 flex-1 truncate text-sm", notification.is_read ? "font-medium" : "font-semibold")}>
              {notification.title}
            </p>
            {/* No popup, hora e badge saem de cena no hover para as ações
                entrarem no mesmo lugar (sem sobreposição). Em touch as ações
                vivem no espaço reservado pelo pr-16 — meta continua visível. */}
            {rollupCount > 0 && (
              <Badge
                variant="secondary"
                className={cn(
                  "h-4 shrink-0 px-1 text-[10px] tabular-nums",
                  variant === "popup" &&
                    "transition-opacity group-hover:opacity-0 [@media(pointer:coarse)]:group-hover:opacity-100"
                )}
              >
                ×{rollupCount}
              </Badge>
            )}
            <span
              className={cn(
                "shrink-0 text-[11px] text-muted-foreground tabular-nums",
                variant === "popup" &&
                  "transition-opacity group-hover:opacity-0 [@media(pointer:coarse)]:group-hover:opacity-100"
              )}
              title={format(new Date(notification.created_at), "dd/MM/yyyy HH:mm", { locale: ptBR })}
            >
              {shortRelativeTime(notification.created_at)}
            </span>

            {variant === "page" && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 shrink-0 text-muted-foreground hover:text-foreground"
                    onClick={(e) => e.stopPropagation()}
                    aria-label="Ações da notificação"
                  >
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                  {!notification.is_read ? (
                    <DropdownMenuItem onSelect={(e) => handleMarkAsRead(e as unknown as Event)}>
                      <Check className="h-4 w-4 mr-2" />
                      Marcar como lida
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem onSelect={(e) => handleMarkAsUnread(e as unknown as Event)}>
                      <Mail className="h-4 w-4 mr-2" />
                      Marcar como não lida
                    </DropdownMenuItem>
                  )}
                  {route && (
                    <DropdownMenuItem onSelect={(e) => handleOpen(e as unknown as Event)}>
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Abrir
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    onSelect={(e) => handleDelete(e as unknown as Event)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Excluir
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2">
            {notification.message}
          </p>
        </div>
      </div>
    </div>
  );
}
